import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import ProductCard from '../components/product/ProductCard';
import ComboCard from '../components/product/ComboCard';
import './Wishlist.css';

const Wishlist = () => {
  const { isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bestsellers, setBestsellers] = useState([]);
  const [loadingBestsellers, setLoadingBestsellers] = useState(false);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
    }
    fetchBestsellers();
  }, [isAuthenticated]);

  // Refetch wishlist when items are added/removed on this page (e.g. from ProductCard)
  useEffect(() => {
    const onWishlistUpdated = () => {
      if (isAuthenticated) fetchWishlist();
    };
    window.addEventListener('wishlistUpdated', onWishlistUpdated);
    return () => window.removeEventListener('wishlistUpdated', onWishlistUpdated);
  }, [isAuthenticated]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await api.get('/wishlist');
      setWishlistItems(response.wishlist || []);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBestsellers = async () => {
    try {
      setLoadingBestsellers(true);
      const response = await api.get('/products/bestsellers');
      setBestsellers(response.products || []);
    } catch (error) {
      console.error('Error fetching bestsellers:', error);
    } finally {
      setLoadingBestsellers(false);
    }
  };

  if (loading) return null;

  const comboOnlyItems = wishlistItems.filter((item) => item.combo_only);

  return (
    <div className="wishlist-page">
      <Container>
        <h1 className="wishlist-title">My Wishlist</h1>

        {comboOnlyItems.length > 0 && (
          <Alert variant="warning" className="mb-4">
            <strong>Some wishlist items are combo-only.</strong>{' '}
            {comboOnlyItems[0]?.combo_only_message ||
              'These products cannot be purchased separately — buy the combo instead.'}
          </Alert>
        )}

        {wishlistItems.length === 0 ? (
          <div className="empty-wishlist-section">
            <Card className="empty-wishlist-card">
              <Card.Body className="text-center py-5">
                <p className="empty-wishlist-message">Your wishlist is empty</p>
                <Link to="/products">
                  <Button variant="primary" className="browse-products-btn">Browse Products</Button>
                </Link>
              </Card.Body>
            </Card>

            {/* Bestsellers Section */}
            {bestsellers.length > 0 && (
              <div className="bestsellers-section mt-5">
                <h2 className="bestsellers-title">Bestsellers</h2>
                {loadingBestsellers ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <Row>
                    {bestsellers.map((product) => (
                      <Col key={product.id} md={4} sm={6} xs={6} lg={3} className="mb-4">
                        <ProductCard product={product} buttonText="Add to Cart" />
                      </Col>
                    ))}
                  </Row>
                )}
                <div className="text-center mt-4">
                  <Link to="/products?filter=bestsellers">
                    <Button variant="outline-dark" className="show-more-btn">
                      Show More
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        ) : (
        <Row>
          {wishlistItems.map((item) => {
            if (item.item_type === 'combo' || item.combo_id) {
              const combo = {
                id: item.combo_id,
                title: item.title,
                price: item.price,
                strike_through_price: item.strike_through_price,
                discount_percent: item.discount_percent,
                thumbnail_image: item.thumbnail_image || '/placeholder.png',
                created_at: item.created_at,
                in_wishlist: true,
              };
              return (
                <Col key={`combo-${item.combo_id}`} md={4} sm={6} xs={6} lg={3} className="mb-4">
                  <ComboCard combo={combo} />
                </Col>
              );
            }

            // Transform wishlist item to product format for ProductCard
            const product = {
              id: item.product_id,
              name: item.name,
              price: item.final_price || item.price || 0,
              strike_through_price: item.strike_through_price,
              primary_image: item.product_image || '/placeholder.png',
              category_name: item.category_name || '',
              average_ratings: parseFloat(item.average_ratings) || 0,
              average_rating: parseFloat(item.average_ratings) || 0,
              admin_rating: item.admin_rating != null && item.admin_rating !== '' ? parseFloat(item.admin_rating) : null,
              review_count: parseInt(item.review_count) || 0,
              total_reviews: parseInt(item.review_count) || 0,
              reviewStats: {
                total_reviews: parseInt(item.review_count) || 0,
                average_rating: parseFloat(item.average_ratings) || 0,
              },
              is_bestseller: item.is_bestseller || 0,
              is_new: item.is_new || 0,
              is_sale: item.is_sale || 0,
              in_wishlist: true,
              combo_only: item.combo_only || false,
              combo_only_message: item.combo_only_message || '',
              combo_titles: item.combo_titles || [],
            };

            return (
              <Col key={`product-${item.product_id}`} md={4} sm={6} xs={6} lg={3} className="mb-4">
                <ProductCard product={product} buttonText="Add to Cart" />
              </Col>
            );
          })}
        </Row>
        )}
      </Container>
    </div>
  );
};

export default Wishlist;




